'use client';

import { Box } from '@mui/material';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { theme } from '@repo/ui/Theme';
import { dynamicWithRetry } from '../../../../DynamicWithRetry';
import { CalculatorForm } from './CalculatorForm';
import { createCdCalculatorStore, defaultFormFieldValues } from './CalculatorForm/calculatorStore';
import { defaultTermDaysToTimePeriodMonths } from './CalculatorForm/utils';
import { CalculatorResultsGraphSkeleton } from './CalculatorResultsGraphSkeleton';

const CdCalculatorResultsGraph = dynamicWithRetry<Record<string, never>>(
  () =>
    import('./CdCalculatorResultsGraph/index').then(mod => ({
      default: mod.CdCalculatorResultsGraph,
    })),
  { componentName: 'CdCalculatorResultsGraph' },
);

function CdCalculatorGraphFallback() {
  return (
    <div style={{ height: '100%', minHeight: 0, width: '100%' }}>
      <CalculatorResultsGraphSkeleton />
    </div>
  );
}

type CdCalculatorProps = {
  defaultApy: number;
  defaultTerm: number;
  nationalAvgApy?: number;
};

export function CdCalculator({ defaultApy, defaultTerm, nationalAvgApy }: CdCalculatorProps) {
  useMemo(() => {
    createCdCalculatorStore(
      {
        ...defaultFormFieldValues,
        annualPercentageYield: defaultApy.toString(),
        termLengthUnit: 'months',
        termLength: defaultTermDaysToTimePeriodMonths(defaultTerm),
      },
      { nationalAvgApy },
    );
  }, [defaultApy, defaultTerm, nationalAvgApy]);

  return (
    <FeatureViewed entityName="cd_calculator" entitySection="banking">
      <Box
        data-testid="calculator-container"
        sx={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          padding: {
            xs: '1rem',
            md: '1.5rem',
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'stretch',
            columnGap: '24px',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: { sm: 'nowrap' },
            minWidth: 0,
            rowGap: { xs: 5, sm: '24px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              justifyContent: 'flex-start',
              width: {
                xs: '100%',
                sm: `225px`,
                md: `300px`,
              },
            }}
          >
            <CalculatorForm />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: { xs: '1 1 0%', sm: '1 1 0%' },
              flexDirection: 'column',
              justifyContent: 'flex-start',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Box
              data-testid="calculator-results-graph"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                justifyContent: 'flex-start',
                marginLeft: 0,
                minHeight: 0,
                minWidth: 0,
                overflow: 'visible',
                width: '100%',
                '& .recharts-responsive-container': { display: 'block', lineHeight: 0 },
                '& .recharts-surface': {
                  overflow: 'visible',
                  position: 'absolute',
                  top: 0,
                },
                '& .recharts-surface:focus': {
                  outlineColor: theme.palette.blues.light,
                },
                '& .recharts-wrapper': {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  justifyContent: 'flex-start',
                  minHeight: 0,
                },
                '& g': {
                  outline: theme.palette.blues.light,
                },
                '& .cd-summary-bar-category-label': {
                  color: '#3D3D3B !important',
                  fill: '#3D3D3B !important',
                },
                '& .cd-summary-bar-category-label tspan': {
                  fill: '#3D3D3B !important',
                },
              }}
            >
              <ErrorBoundary fallback={<CdCalculatorGraphFallback />}>
                <Suspense fallback={<CdCalculatorGraphFallback />}>
                  <CdCalculatorResultsGraph />
                </Suspense>
              </ErrorBoundary>
            </Box>
          </Box>
        </Box>
      </Box>
    </FeatureViewed>
  );
}
